import { Component, effect, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ServicePortalApi } from '../../../api/api';
import { CustomerAccountInput } from '../../../api/types';
import { SynBreadcrumbComponent, SynBreadcrumbItemComponent } from '@synergy-design-system/angular';
import { CustomerAccountFormComponent } from './customer-account-form.component';
import { getUserData } from '../../../api/auth';
import { handleError, setDuplicateUsernameError } from '../../../errors';

@Component({
  selector: 'app-new-customer-account',
  template: `
    <syn-breadcrumb>
      <syn-breadcrumb-item routerLink="" i18n="@@home">Home</syn-breadcrumb-item>
      <syn-breadcrumb-item routerLink="/customers" i18n="@@customer-accounts">Customer Accounts</syn-breadcrumb-item>
      <syn-breadcrumb-item i18n="@@create-new-customer-account">Create a New Customer Account</syn-breadcrumb-item>
    </syn-breadcrumb>

    <h1 i18n="@@create-new-customer-account">Create a New Customer Account</h1>

    <app-customer-account-form
      [account]="emptyAccount"
      [ssus]="ssus()"
      submitIcon="save"
      submitText="Create a New Customer Account"
      i18n-submitText="@@create-new-customer-account"
      [canEdit]="true"
      [create]="true"
      [isAdmin]="isAdmin()"
      (submitAccount)="onSubmit($event)"
    />
  `,
  styles: [],
  imports: [
    RouterLink,
    SynBreadcrumbComponent,
    SynBreadcrumbItemComponent,
    CustomerAccountFormComponent,
  ],
})
export class NewCustomerAccountComponent {
  clientId = inject(ActivatedRoute).snapshot.params['id'];
  api = inject(ServicePortalApi);
  router = inject(Router);

  isAdmin = signal(false);
  ssus = signal<string[]>([]);

  emptyAccount: CustomerAccountInput = {
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    enabled: true,
    emailVerified: false,
    note: '',
    type: 'customer',
    salutation: '-',
  };

  constructor() {
    const userData = getUserData();
    this.isAdmin.set(userData.roles.includes('sickid_usermanager_admin'));

    // SSUs must fetched only when the user is an admin
    effect(() => {
      if (this.isAdmin()) {
        this.api.getSsus(({ ssus }) => {
          this.ssus.set(ssus.toSorted());
        });

        // Use admin's SSU code as default value for new account
        this.emptyAccount.ssuCode = userData.ssuCode ?? '';
      }
      if (userData.managedSsu && userData.managedSsu.trim().length > 0) {
        const ssus = userData.managedSsu
          .split(',')
          .map((s) => s.trim())
          .filter((s) => s.length > 0);
        this.ssus.set(ssus.toSorted());
        // Use users SSU code as default value for new account
        this.emptyAccount.ssuCode = userData.ssuCode ?? '';
      }
    });
  }

  onSubmit(account: CustomerAccountInput) {
    this.api.createCustomer(account, (createdAccount) => {
        void this.router.navigate(['customers', createdAccount.id]);
      },
      (error) => {
        if (error.status === 409) {
          setDuplicateUsernameError();
        } else {
          handleError(error);
        }
      });
  }
}
